// Presentation generation tool definition for Claude API
// Full PptxGenJS feature set: text, tables, charts, shapes, images

import type { Tool } from '@anthropic-ai/sdk/resources/messages';

// ============================================================================
// Reusable Schema Fragments
// ============================================================================

const shadowSchema = {
  type: 'object',
  properties: {
    type: { type: 'string', enum: ['outer', 'inner'] },
    color: { type: 'string', description: '6-char hex, no # prefix' },
    blur: { type: 'number', description: '0-100' },
    offset: { type: 'number', description: '0-200, must be non-negative' },
    angle: { type: 'number', description: '0-359 degrees (135=bottom-right, 270=upward)' },
    opacity: { type: 'number', description: '0.0-1.0' },
  },
};

const positionProps = {
  x: { type: 'number', description: 'X position in inches' },
  y: { type: 'number', description: 'Y position in inches' },
  w: { type: 'number', description: 'Width in inches' },
  h: { type: 'number', description: 'Height in inches' },
};

// ============================================================================
// Element Schemas
// ============================================================================

const titleElementSchema = {
  type: 'object',
  properties: {
    type: { type: 'string', enum: ['title'] },
    text: { type: 'string' },
    color: { type: 'string', description: '6-char hex, no # prefix' },
    fontFace: { type: 'string' },
    fontSize: { type: 'number' },
    align: { type: 'string', enum: ['left', 'center', 'right'] },
  },
  required: ['type', 'text'],
};

const subtitleElementSchema = {
  type: 'object',
  properties: {
    type: { type: 'string', enum: ['subtitle'] },
    text: { type: 'string' },
    color: { type: 'string' },
    fontFace: { type: 'string' },
    fontSize: { type: 'number' },
    align: { type: 'string', enum: ['left', 'center', 'right'] },
  },
  required: ['type', 'text'],
};

const textRunSchema = {
  type: 'object',
  properties: {
    text: { type: 'string' },
    bold: { type: 'boolean' },
    italic: { type: 'boolean' },
    underline: { type: 'boolean' },
    color: { type: 'string' },
    fontSize: { type: 'number' },
    fontFace: { type: 'string' },
    breakLine: { type: 'boolean', description: 'Add line break after this run. Required between multi-line items.' },
    hyperlink: { type: 'object', properties: { url: { type: 'string' } } },
  },
  required: ['text'],
};

const textElementSchema = {
  type: 'object',
  properties: {
    type: { type: 'string', enum: ['text'] },
    text: {
      description: 'Simple string or array of TextRun objects for rich/multi-line text',
      oneOf: [
        { type: 'string' },
        { type: 'array', items: textRunSchema },
      ],
    },
    ...positionProps,
    fontSize: { type: 'number' },
    fontFace: { type: 'string' },
    bold: { type: 'boolean' },
    italic: { type: 'boolean' },
    underline: { type: 'boolean' },
    color: { type: 'string', description: '6-char hex, no # prefix' },
    align: { type: 'string', enum: ['left', 'center', 'right'] },
    valign: { type: 'string', enum: ['top', 'middle', 'bottom'] },
    charSpacing: { type: 'number' },
    margin: { type: 'number', description: 'Internal padding in points. Set 0 to align precisely with shapes.' },
    hyperlink: { type: 'object', properties: { url: { type: 'string' } } },
  },
  required: ['type', 'text'],
};

const bulletItemSchema = {
  oneOf: [
    { type: 'string' },
    {
      type: 'object',
      properties: {
        text: { type: 'string' },
        bold: { type: 'boolean' },
        italic: { type: 'boolean' },
        color: { type: 'string' },
        fontSize: { type: 'number' },
        fontFace: { type: 'string' },
        indentLevel: { type: 'number', description: '0=top level, 1=sub-item, 2=sub-sub-item' },
      },
      required: ['text'],
    },
  ],
};

const bulletsElementSchema = {
  type: 'object',
  properties: {
    type: { type: 'string', enum: ['bullets'] },
    items: { type: 'array', items: bulletItemSchema },
    ...positionProps,
    fontSize: { type: 'number' },
    fontFace: { type: 'string' },
    color: { type: 'string' },
    paraSpaceAfter: { type: 'number', description: 'Space after each bullet in points' },
  },
  required: ['type', 'items'],
};

const numberedElementSchema = {
  type: 'object',
  properties: {
    type: { type: 'string', enum: ['numbered'] },
    items: { type: 'array', items: bulletItemSchema },
    ...positionProps,
    fontSize: { type: 'number' },
    fontFace: { type: 'string' },
    color: { type: 'string' },
    paraSpaceAfter: { type: 'number' },
  },
  required: ['type', 'items'],
};

const imageElementSchema = {
  type: 'object',
  properties: {
    type: { type: 'string', enum: ['image'] },
    src: { type: 'string', description: 'Base64 data URI (image/png;base64,...) or URL' },
    ...positionProps,
    sizing: {
      type: 'object',
      description: 'Image sizing mode: contain (fit), cover (fill+crop), crop (specific area)',
      properties: {
        type: { type: 'string', enum: ['contain', 'cover', 'crop'] },
        w: { type: 'number' }, h: { type: 'number' },
        x: { type: 'number', description: 'crop only' },
        y: { type: 'number', description: 'crop only' },
      },
      required: ['type'],
    },
    rounding: { type: 'boolean', description: 'Circular crop' },
    rotate: { type: 'number', description: '0-359 degrees' },
    transparency: { type: 'number', description: '0-100' },
    altText: { type: 'string', description: 'Accessibility text' },
    hyperlink: { type: 'object', properties: { url: { type: 'string' } } },
  },
  required: ['type', 'src'],
};

const shapeElementSchema = {
  type: 'object',
  properties: {
    type: { type: 'string', enum: ['shape'] },
    shape: { type: 'string', enum: ['rect', 'ellipse', 'arrow', 'line', 'roundedRect'], description: 'Shape type. Use roundedRect with rectRadius for rounded corners.' },
    ...positionProps,
    color: { type: 'string', description: 'Fill color (6-char hex, no # prefix)' },
    transparency: { type: 'number', description: '0-100' },
    line: {
      type: 'object',
      properties: {
        color: { type: 'string' },
        width: { type: 'number' },
        dashType: { type: 'string', enum: ['solid', 'dash', 'dot', 'dashDot'] },
      },
    },
    rectRadius: { type: 'number', description: 'Corner radius for roundedRect (inches)' },
    shadow: shadowSchema,
  },
  required: ['type', 'shape', 'x', 'y', 'w', 'h'],
};

const tableCellSchema = {
  oneOf: [
    { type: 'string' },
    {
      type: 'object',
      properties: {
        text: { type: 'string' },
        options: {
          type: 'object',
          properties: {
            bold: { type: 'boolean' },
            italic: { type: 'boolean' },
            color: { type: 'string' },
            fontSize: { type: 'number' },
            fontFace: { type: 'string' },
            fill: { type: 'object', properties: { color: { type: 'string' } } },
            align: { type: 'string', enum: ['left', 'center', 'right'] },
            valign: { type: 'string', enum: ['top', 'middle', 'bottom'] },
            colspan: { type: 'number' },
            rowspan: { type: 'number' },
            border: {
              oneOf: [
                { type: 'object', properties: { pt: { type: 'number' }, color: { type: 'string' } } },
                { type: 'array', items: { type: 'object', properties: { pt: { type: 'number' }, color: { type: 'string' } } } },
              ],
            },
          },
        },
      },
      required: ['text'],
    },
  ],
};

const tableElementSchema = {
  type: 'object',
  properties: {
    type: { type: 'string', enum: ['table'] },
    rows: {
      type: 'array',
      description: 'Array of rows, each row is array of cells (string or {text, options})',
      items: { type: 'array', items: tableCellSchema },
    },
    ...positionProps,
    colW: { type: 'array', items: { type: 'number' }, description: 'Column widths in inches' },
    rowH: { type: 'array', items: { type: 'number' }, description: 'Row heights in inches' },
    border: { type: 'object', properties: { pt: { type: 'number' }, color: { type: 'string' } } },
    fill: { type: 'object', properties: { color: { type: 'string' } } },
    fontSize: { type: 'number' },
    fontFace: { type: 'string' },
    color: { type: 'string' },
    autoPage: { type: 'boolean' },
  },
  required: ['type', 'rows'],
};

const chartElementSchema = {
  type: 'object',
  properties: {
    type: { type: 'string', enum: ['chart'] },
    chartType: { type: 'string', enum: ['bar', 'line', 'pie', 'doughnut', 'scatter', 'bubble', 'radar'] },
    data: {
      type: 'array',
      description: 'Chart data series',
      items: {
        type: 'object',
        properties: {
          name: { type: 'string' },
          labels: { type: 'array', items: { type: 'string' } },
          values: { type: 'array', items: { type: 'number' } },
          sizes: { type: 'array', items: { type: 'number' }, description: 'For bubble charts only' },
        },
        required: ['name', 'labels', 'values'],
      },
    },
    ...positionProps,
    barDir: { type: 'string', enum: ['col', 'bar'], description: 'col=vertical bars, bar=horizontal' },
    barGrouping: { type: 'string', enum: ['clustered', 'stacked', 'percentStacked'] },
    lineSize: { type: 'number' },
    lineSmooth: { type: 'boolean' },
    showTitle: { type: 'boolean' },
    title: { type: 'string' },
    showLegend: { type: 'boolean' },
    legendPos: { type: 'string', enum: ['b', 't', 'l', 'r', 'tr'] },
    showValue: { type: 'boolean' },
    showPercent: { type: 'boolean' },
    dataLabelPosition: { type: 'string', enum: ['outEnd', 'inEnd', 'center'] },
    dataLabelColor: { type: 'string' },
    chartColors: { type: 'array', items: { type: 'string' }, description: 'Hex colors for series (no # prefix)' },
    chartArea: {
      type: 'object',
      properties: {
        fill: { type: 'object', properties: { color: { type: 'string' } } },
        roundedCorners: { type: 'boolean' },
      },
    },
    catAxisLabelColor: { type: 'string' },
    valAxisLabelColor: { type: 'string' },
    valGridLine: {
      type: 'object',
      properties: { color: { type: 'string' }, size: { type: 'number' }, style: { type: 'string', enum: ['solid', 'dash', 'dot', 'none'] } },
    },
    catGridLine: {
      type: 'object',
      properties: { color: { type: 'string' }, size: { type: 'number' }, style: { type: 'string', enum: ['solid', 'dash', 'dot', 'none'] } },
    },
  },
  required: ['type', 'chartType', 'data'],
};

// ============================================================================
// Slide Master Schema
// ============================================================================

const slideMasterSchema = {
  type: 'object',
  properties: {
    title: { type: 'string', description: 'Master name (referenced by slides via masterName)' },
    background: {
      type: 'object',
      properties: {
        color: { type: 'string' },
        data: { type: 'string', description: 'Base64 data URI' },
        path: { type: 'string', description: 'Image URL' },
      },
    },
    objects: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          placeholder: {
            type: 'object',
            properties: {
              options: {
                type: 'object',
                properties: {
                  name: { type: 'string' },
                  type: { type: 'string', enum: ['title', 'body', 'subtitle'] },
                  x: { type: 'number' }, y: { type: 'number' }, w: { type: 'number' }, h: { type: 'number' },
                },
                required: ['name', 'type', 'x', 'y', 'w', 'h'],
              },
            },
          },
        },
      },
    },
  },
  required: ['title'],
};

// ============================================================================
// Slide Schema
// ============================================================================

const slideSchema = {
  type: 'object',
  properties: {
    layout: {
      type: 'string',
      enum: ['title', 'content', 'two-column', 'blank'],
      description: 'Slide layout type (hint for preview, does not constrain elements)',
    },
    backgroundColor: {
      type: 'string',
      description: 'Background color (6-char hex, no # prefix)',
    },
    backgroundImage: {
      type: 'string',
      description: 'Background image (base64 data URI or URL)',
    },
    notes: {
      type: 'string',
      description: 'Speaker notes for this slide',
    },
    masterName: {
      type: 'string',
      description: 'Name of a defined slide master to use',
    },
    elements: {
      type: 'array',
      description: 'Slide elements: title, subtitle, text, bullets, numbered, image, shape, table, chart',
      items: {
        oneOf: [
          titleElementSchema,
          subtitleElementSchema,
          textElementSchema,
          bulletsElementSchema,
          numberedElementSchema,
          imageElementSchema,
          shapeElementSchema,
          tableElementSchema,
          chartElementSchema,
        ],
      },
    },
  },
  required: ['elements'],
};

// ============================================================================
// Example JSON
// ============================================================================

const presentationExampleJson = `{
  "type": "presentation",
  "title": "Quarterly Sales Report",
  "author": "Analytics Team",
  "layout": "LAYOUT_16x9",
  "slides": [
    {
      "layout": "title",
      "backgroundColor": "1E2761",
      "elements": [
        { "type": "title", "text": "Q4 Sales Report", "color": "FFFFFF", "fontFace": "Georgia", "align": "center" },
        { "type": "subtitle", "text": "Building momentum into 2025", "color": "CADCFC" }
      ],
      "notes": "Welcome everyone to the quarterly review"
    },
    {
      "layout": "content",
      "elements": [
        { "type": "title", "text": "Revenue by Region" },
        { "type": "chart", "chartType": "bar", "data": [
          { "name": "Revenue ($M)", "labels": ["North", "South", "East", "West"], "values": [4.5, 3.2, 5.8, 2.9] }
        ], "x": 0.5, "y": 1.5, "w": 9, "h": 3.5, "barDir": "col", "chartColors": ["1E2761", "3B5998", "6B8DD6", "CADCFC"] }
      ]
    },
    {
      "layout": "content",
      "elements": [
        { "type": "title", "text": "Key Metrics" },
        { "type": "table", "rows": [
          [{ "text": "Metric", "options": { "bold": true, "fill": { "color": "1E2761" }, "color": "FFFFFF" } }, { "text": "Q3", "options": { "bold": true, "fill": { "color": "1E2761" }, "color": "FFFFFF" } }, { "text": "Q4", "options": { "bold": true, "fill": { "color": "1E2761" }, "color": "FFFFFF" } }],
          ["Revenue", "$14.2M", "$16.4M"],
          ["Customers", "1,247", "1,412"],
          ["NPS Score", "72", "78"]
        ], "x": 1, "y": 1.8, "w": 8, "colW": [3, 2.5, 2.5], "border": { "pt": 1, "color": "CCCCCC" } }
      ]
    },
    {
      "layout": "content",
      "elements": [
        { "type": "title", "text": "Next Steps" },
        { "type": "bullets", "items": [
          { "text": "Expand West Coast operations", "bold": true },
          { "text": "Target 15% growth in Q1", "indentLevel": 1 },
          "Launch partner program in March",
          "Hire 3 additional sales reps"
        ], "fontSize": 16, "paraSpaceAfter": 8 }
      ]
    }
  ]
}`;

// ============================================================================
// Tool Definition
// ============================================================================

export const PRESENTATION_GENERATION_TOOL: Tool = {
  name: 'generate_presentation',
  description: `Generate a PowerPoint presentation (.pptx). Use this tool when the user asks you to create a presentation, slideshow, pitch deck, or similar.

CRITICAL RULES FOR COLORS:
- NEVER use "#" prefix on hex colors — it corrupts the file. Use "FF0000" not "#FF0000"
- NEVER encode opacity in hex (no 8-char colors like "00000020"). Use opacity property instead.
- Use "bullet: true" — NEVER unicode "•" (creates double bullets)
- Use "breakLine: true" between text array items for multi-line text

Element types available:
- title: Slide title text (enhanced: color, fontFace, fontSize, align)
- subtitle: Secondary text (enhanced: color, fontFace, fontSize, align)
- text: Positioned text box — supports rich text arrays with mixed formatting
- bullets: Bulleted list — supports indentLevel, per-item styling
- numbered: Numbered list — same features as bullets
- image: Image from base64/URL — supports sizing modes (contain/cover/crop), rounding, rotation, hyperlinks
- shape: Shapes (rect, ellipse, arrow, line, roundedRect) — supports shadows, transparency, line styles
- table: Data tables — supports cell styling, merged cells (colspan), borders, fill colors
- chart: Charts (bar, line, pie, doughnut, scatter, bubble, radar) — supports custom colors, labels, legends

Slide-level features:
- backgroundColor: Slide background color
- backgroundImage: Base64/URL background image
- notes: Speaker notes
- masterName: Reference to a defined slide master

Presentation-level features:
- layout: LAYOUT_16x9 (default), LAYOUT_16x10, LAYOUT_4x3, LAYOUT_WIDE
- author: Presentation author metadata
- masters: Define reusable slide masters

Example presentation:
${presentationExampleJson}

Design tips:
- Pick a bold color palette that matches the topic (don't default to blue)
- Use dark backgrounds for title slides, light for content
- Every slide needs a visual element (chart, table, shape, or icon)
- Vary layouts across slides — don't repeat the same structure
- Use 36-44pt for titles, 14-16pt for body text
- Keep 0.5" minimum margins from edges
- When editing an existing presentation, include the presentationId from get_content`,
  input_schema: {
    type: 'object' as const,
    properties: {
      presentationId: {
        type: 'string',
        description: 'If editing an existing presentation, set this to the content ID returned by get_content. Omit for new presentations.',
      },
      type: {
        type: 'string',
        enum: ['presentation'],
        description: 'Must be "presentation"',
      },
      title: {
        type: 'string',
        description: 'Presentation title',
      },
      author: {
        type: 'string',
        description: 'Presentation author',
      },
      layout: {
        type: 'string',
        enum: ['LAYOUT_16x9', 'LAYOUT_16x10', 'LAYOUT_4x3', 'LAYOUT_WIDE'],
        description: 'Slide layout dimensions. Default: LAYOUT_16x9 (10" × 5.625")',
      },
      slides: slideSchema.properties.elements.type === 'array'
        ? { type: 'array', description: 'Array of slides', items: slideSchema }
        : { type: 'array', items: slideSchema },
      masters: {
        type: 'array',
        description: 'Slide master definitions (optional)',
        items: slideMasterSchema,
      },
    },
    required: ['type', 'slides'],
  },
};
